import { useState } from 'react';
import { X, Mail, Users, Building2, Globe, UserPlus, Loader2, Check } from 'lucide-react';
import type { MeetingDetailResponse } from '../types';

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (contentType: 'full' | 'summary', recipientType: string, customRecipients?: string[]) => Promise<void>;
  meetingData: MeetingDetailResponse;
}

export function EmailModal({ isOpen, onClose, onSend, meetingData }: EmailModalProps) {
  const [contentType, setContentType] = useState<'full' | 'summary'>('summary');
  const [recipientType, setRecipientType] = useState<'internal' | 'external' | 'all' | 'custom'>('all');
  const [customEmails, setCustomEmails] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    try {
      setIsSending(true);
      setError(null);

      let customRecipients: string[] | undefined;
      if (recipientType === 'custom') {
        // Parse custom emails
        customRecipients = customEmails
          .split(/[,;\s]+/)
          .map(e => e.trim())
          .filter(e => e.includes('@'));

        if (customRecipients.length === 0) {
          setError('Lütfen en az bir geçerli email adresi girin');
          setIsSending(false);
          return;
        }
      }

      await onSend(contentType, recipientType, customRecipients);

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Email gönderilemedi');
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  // Get participant count
  const participants = meetingData.meeting.participants || [];
  const internalCount = participants.filter((p: any) => p.email?.toLowerCase().endsWith('@logo.com.tr')).length;
  const externalCount = participants.length - internalCount;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail size={24} />
            <div>
              <h2 className="text-xl font-semibold">Email Gönder</h2>
              <p className="text-sm text-blue-100 mt-1">{meetingData.meeting.subject}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <Check className="text-green-600" size={20} />
              <p className="text-green-800 font-medium">Email başarıyla gönderildi!</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Content Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              İçerik Türü
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setContentType('summary')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  contentType === 'summary'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    contentType === 'summary' ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {contentType === 'summary' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-900">📋 Özet</div>
                    <div className="text-xs text-slate-500">Yapay zeka özeti</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setContentType('full')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  contentType === 'full'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    contentType === 'full' ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {contentType === 'full' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-900">📄 Tam Transkript</div>
                    <div className="text-xs text-slate-500">Tüm konuşma</div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Recipient Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              Alıcılar
            </label>
            <div className="space-y-3">
              {/* All Participants */}
              <button
                onClick={() => setRecipientType('all')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  recipientType === 'all'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    recipientType === 'all' ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {recipientType === 'all' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <Users size={18} className="text-slate-600 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">Tüm Katılımcılar</div>
                    <div className="text-xs text-slate-500">
                      {participants.length} kişiye gönderilecek
                    </div>
                  </div>
                </div>
              </button>

              {/* Internal Only */}
              <button
                onClick={() => setRecipientType('internal')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  recipientType === 'internal'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    recipientType === 'internal' ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {recipientType === 'internal' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <Building2 size={18} className="text-slate-600 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">Organizasyon İçi</div>
                    <div className="text-xs text-slate-500">
                      Sadece @logo.com.tr ({internalCount} kişi)
                    </div>
                  </div>
                </div>
              </button>

              {/* External Only */}
              <button
                onClick={() => setRecipientType('external')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  recipientType === 'external'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    recipientType === 'external' ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {recipientType === 'external' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <Globe size={18} className="text-slate-600 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">Organizasyon Dışı</div>
                    <div className="text-xs text-slate-500">
                      Harici katılımcılar ({externalCount} kişi)
                    </div>
                  </div>
                </div>
              </button>

              {/* Custom Recipients */}
              <button
                onClick={() => setRecipientType('custom')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  recipientType === 'custom'
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    recipientType === 'custom' ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {recipientType === 'custom' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <UserPlus size={18} className="text-slate-600 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">Seçtiğim Alıcılar</div>
                    <div className="text-xs text-slate-500">
                      Manuel olarak email adresi ekle
                    </div>
                  </div>
                </div>
              </button>

              {/* Custom Email Input */}
              {recipientType === 'custom' && (
                <div className="ml-8 mt-3">
                  <textarea
                    value={customEmails}
                    onChange={(e) => setCustomEmails(e.target.value)}
                    placeholder="Email adreslerini virgül, noktalı virgül veya boşluk ile ayırarak girin&#10;Örnek: ahmet@example.com, mehmet@example.com"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
                    rows={4}
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    💡 Birden fazla email adresini virgül, noktalı virgül veya boşluk ile ayırabilirsiniz
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-6 bg-slate-50 rounded-b-2xl flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={isSending}
            className="px-6 py-2.5 text-slate-600 hover:bg-slate-200 rounded-xl transition-colors disabled:opacity-50"
          >
            İptal
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || success}
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg shadow-blue-500/25 disabled:opacity-50 flex items-center gap-2"
          >
            {isSending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Gönderiliyor...</span>
              </>
            ) : success ? (
              <>
                <Check size={18} />
                <span>Gönderildi</span>
              </>
            ) : (
              <>
                <Mail size={18} />
                <span>Gönder</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
